import { useRouter } from 'next/router';
import { NavigateProps } from '../Navigate/props';
import { useEffect } from 'react';

/**
 * NextNavigate Component
 * A component that handles client-side navigation in Next.js applications.
 * Uses Next.js router to programmatically navigate to a new route on mount.
 * Supports both push and replace navigation modes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.to - The destination path to navigate to
 * @param {boolean} [props.replace=false] - Whether to replace current history entry
 * @returns {null} Component renders nothing, only handles navigation
 *
 * @example
 * // Navigate to new route, adding to history
 * <NextNavigate to="/dashboard" />
 *
 * // Replace current route in history
 * <NextNavigate to="/login" replace={true} />
 */
export function NextNavigate({ to, replace = false }: NavigateProps): null {
  const router = useRouter();

  /**
   * Handles navigation on component mount
   * Uses router.replace() if replace=true, otherwise router.push()
   */
  function onMount() {
    if (replace) {
      router.replace(to);
      return;
    }

    router.push(to);
  }

  useEffect(onMount, [replace, router, to]);

  return null;
}
